import affirm from '../core/Affirm'
import algo from '../core/Algo'
import { IFlow, INode, INodeOutputContext, NodeInputValue, NodeType } from '../definitions/Nodes'

export const NODE_UTILS = {
    /**
     * All the values that are received by the node while in execution.
     * You have one entry per each different node that is connected to this one.
     */
    extractInputs: <T extends NodeType>(args: INodeOutputContext<NodeType>[], currentNode: INode<T>): NodeInputValue<T>[] => {
        affirm(args, 'Invalid args')
        affirm(currentNode, 'Invalid node')

        return currentNode.inputs
            .filter(input => !(currentNode.type === 'end_fork' && args.some(a => a.node.uuid !== input.uuid)))
            .map(input => {
                affirm(args.some(x => x.node.uuid === input.uuid), `Unable to find input value: node "${currentNode.name}" looking for "${input.uuid}"`)

                const inputNodeArgs = args.find(x => x.node.uuid === input.uuid)

                const mappedArgument = {
                    _fromNode: input.uuid,
                    _fromNodeType: inputNodeArgs.node.type,
                    _fromNodeName: inputNodeArgs.node.name,
                    _outputKey: input.outputKey,
                    _inputKey: input.argumentKey,
                    _schema: inputNodeArgs.metadata
                } as NodeInputValue<T>
                mappedArgument[input.argumentKey] = inputNodeArgs.output[input.outputKey]
                return mappedArgument
            })
    },
    validationError: <T extends NodeType>(node: INode<T>, errors: string[]) => {
        return new Error(`Validation error in node '${node.name}'(${node.uuid}):\n${errors.map(x => '  - ' + x).join('; \n')}`)
    },
    getPlaceholders: (node: INode<'xfrm_sql'>) => {
        affirm(node, 'Invalid node')
        affirm(node.data, 'Invalid node data')
        affirm(node.data.text, 'Invalid node data text')
        const { data: { text } } = node
        const placeholders: string[] = []

        const indexes = algo.locations(text, '@{')
        for (let i = 0; i < indexes.length; i++) {
            const start = indexes[i]
            const end = text.substring(start).indexOf('}') + start + 1
            const raw = text.substring(start, end)
            const clean = raw.replace('@{', '').replace('}', '')
            placeholders.push(clean)
        }
        return placeholders
    },
    /**
     *
     * @param placeholder ${<node id>.<node output key>.<...field key in node output>}
     * @param inputValues
     * @returns
     */
    findPlaceholderMatch: (placeholder: string, inputValues: NodeInputValue<'xfrm_sql'>[], globals: IFlow['globals']) => {
        affirm(placeholder.includes('.'), `Invalid format for query placeholder: "${placeholder}" isn't formatted correctly (must include ".")`)
        const parts = placeholder.split('.')

        if (parts[0] === 'globals') {
            affirm(globals, `Global parameters are not set in this flow`)

            let currentParam = globals
            for (let i = 1; i < parts.length; i++) {
                const part = parts[i]
                const newParam = currentParam[part]

                affirm(newParam, `Global placeholder parameter value not present: placeholder "${placeholder}" failed at part "${part}"`)
                currentParam = newParam
            }

            return currentParam
        } else {
            affirm(parts.length > 2, `Wrongly formatted placeholder: excpected format is {node id}.{output key}.{fields of output...}, but received "${placeholder}"`)

            const inputsOfNode = inputValues.filter(x => x._fromNode === parts[0])
            affirm(inputsOfNode.length > 0, `Placeholder parameter not found: searching for the node ${parts[0]} but it wasn't available in the input values`)

            const inputOfNodeOutput = inputsOfNode.find(x => x._outputKey === parts[1])
            affirm(inputOfNodeOutput, `Placeholder parameter not found: searching for the output key "${parts[1]}" in node "${parts[0]}" but it wasn't available in the input values`)
            affirm(inputOfNodeOutput.parameter, `Placeholder parameter value not present in input values: looking for "${parts[0]}.${parts[1]}", but got inputs "${inputValues.map(x => x._fromNode + '.' + x._outputKey).join(', ')}"`)

            let currentParam = inputOfNodeOutput.parameter
            for (let i = 2; i < parts.length; i++) {
                const part = parts[i]
                const newParam = currentParam[part]

                affirm(newParam, `Placeholder parameter value not present: placeholder "${placeholder}" failed at part "${part}"`)
                currentParam = newParam
            }
            return currentParam
        }
    },
    replacePlaceholders: (text: string, placeholders: string[], values: any[]) => {
        affirm(text, 'Invalid query text')
        affirm(placeholders.length === values.length, `Mismatch in number of placeholders and placeholder values in query text: ${placeholders.length} !== ${values.length}`)
        let query = text

        for (let i = 0; i < placeholders.length; i++) {
            const ph = placeholders[i]
            const m = values[i]

            query = query.replace('@{' + ph + '}', m)
        }
        return query
    }
}
